import React, { useEffect, useState } from "react";
import axios from "axios";
import { SiLeetcode } from "react-icons/si";
import { motion } from "framer-motion";
import { leetcodeApi, profileLink } from "../config/config";

const LeetCodeDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [contestData, setContestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch LeetCode data from ass_server backend
  const fetchLeetCodeData = async () => {
    try {
      const response = await axios.get(leetcodeApi);
      const data = response.data;

      if (!data || !data.profileData) {
        throw new Error("Invalid data format from backend");
      }

      setUserData(data.profileData);
      setContestData(data.contestData);
      
      // Save to local cache as a secondary fallback
      localStorage.setItem("leetcode_profile_cache", JSON.stringify(data.profileData));
      localStorage.setItem("leetcode_contest_cache", JSON.stringify(data.contestData));
      
      setLoading(false);
    } catch (err) {
      console.error(err);
      
      // Fallback to local cache if backend is down
      const cachedProfile = localStorage.getItem("leetcode_profile_cache");
      const cachedContest = localStorage.getItem("leetcode_contest_cache");
      
      if (cachedProfile) {
        setUserData(JSON.parse(cachedProfile));
        if (cachedContest) setContestData(JSON.parse(cachedContest));
        setLoading(false);
      } else {
        setError("Failed to fetch data from backend");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchLeetCodeData();
  }, []);

  if (loading) return <LoadingMessage />;

  // If there is an error, show the redirect link
  if (error) {
    return (
      <div className="flex justify-center items-center py-10 w-full px-4">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl text-center max-w-md w-full">
          <SiLeetcode className="text-red-400 text-5xl mx-auto mb-4 opacity-80" />
          <h3 className="text-xl font-bold text-white mb-2">LeetCode API Unavailable</h3>
          <p className="text-gray-400 text-sm mb-6">
            The free LeetCode stats API is currently unreachable. You can still view my real-time stats directly on my profile!
          </p>
          <a
            href={profileLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 font-semibold rounded-full border border-blue-500/30 transition-all duration-300"
          >
            Visit LeetCode Profile
          </a>
        </div>
      </div>
    );
  }

  // Destructure required data from userData (Alfa LeetCode API format)
  const {
    totalSolved,
    totalQuestions,
    easySolved,
    totalEasy,
    mediumSolved,
    totalMedium,
    hardSolved,
    totalHard,
    ranking,
  } = userData;

  const totalSub = userData.totalSubmissions?.[0]?.submissions || 1;
  const acSub = userData.matchedUserStats?.acSubmissionNum?.[0]?.submissions || 0;
  const acceptanceRate = ((acSub / totalSub) * 100).toFixed(2);

  // Robust fallback: Use live API data, then cached data, then a known recent snapshot (1637) so it never says N/A
  let contestRating = "1637"; // Known snapshot as fallback
  if (contestData && contestData.contestRating) {
    contestRating = Math.round(contestData.contestRating).toString();
  } else if (localStorage.getItem("leetcode_contest_cache")) {
    const cached = JSON.parse(localStorage.getItem("leetcode_contest_cache"));
    if (cached && cached.contestRating) contestRating = Math.round(cached.contestRating).toString();
  }

  return (
    <div className="flex justify-center items-center">
      <motion.div
        className="rounded-lg w-full max-w-3xl"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="relative group flex items-center justify-center">
          <a href={profileLink} target="_blank" rel="noopener noreferrer">
            <SiLeetcode className="text-blue-600 text-4xl mr-2" />
          </a>
          <h1 className="text-white text-3xl font-bold mb-0">LeetCode</h1>

          {/* Hover Banner */}
          <div className="absolute bottom-10 bg-gray-700 text-white text-sm py-1 px-3 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Go to LeetCode Profile
          </div>
        </div>

        {/* Problem Solving Statistics */}
        <h2 className="text-white text-2xl font-semibold mb-4 text-center">
          Problem Solving Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Left Column: Overall Stats */}
          <motion.div
            className="p-6 rounded-[1.5rem] bg-white/5 border border-white/5 shadow-lg flex flex-col justify-center gap-5"
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white font-semibold">Total Solved</span>
                <span className="text-white font-bold">{totalSolved} <span className="text-gray-400 font-normal">/ {totalQuestions}</span></span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden shadow-inner">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(totalSolved / totalQuestions) * 100}%` }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="bg-blue-500 h-full rounded-full shadow-[0_0_10px_rgba(59,130,246,0.8)]" 
                />
              </div>
            </div>

            <div className="flex justify-between items-center bg-white/5 p-3.5 rounded-xl border border-white/5">
              <span className="text-gray-300 font-medium text-sm">Acceptance Rate</span>
              <span className="text-white font-bold">{acceptanceRate}%</span>
            </div>
            
            <div className="flex justify-between items-center bg-white/5 p-3.5 rounded-xl border border-white/5">
              <span className="text-gray-300 font-medium text-sm">Global Ranking</span>
              <span className="text-white font-bold">#{ranking.toLocaleString()}</span>
            </div>

            <div className="flex justify-between items-center bg-white/5 p-3.5 rounded-xl border border-white/5">
              <span className="text-gray-300 font-medium text-sm">Contest Rating</span>
              <span className="text-purple-400 font-bold">{contestRating}</span>
            </div>
          </motion.div>

          {/* Right Column: Difficulty Breakdown */}
          <motion.div
            className="p-6 rounded-[1.5rem] bg-white/5 border border-white/5 shadow-lg flex flex-col justify-center gap-5"
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {/* Easy */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-emerald-400 font-semibold">Easy</span>
                <span className="text-emerald-400 font-bold">{easySolved} <span className="text-gray-500 font-normal text-xs">/ {totalEasy}</span></span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(easySolved / totalEasy) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                  className="bg-emerald-400 h-full rounded-full shadow-[0_0_8px_rgba(52,211,153,0.8)]" 
                />
              </div>
            </div>

            {/* Medium */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-amber-400 font-semibold">Medium</span>
                <span className="text-amber-400 font-bold">{mediumSolved} <span className="text-gray-500 font-normal text-xs">/ {totalMedium}</span></span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(mediumSolved / totalMedium) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
                  className="bg-amber-400 h-full rounded-full shadow-[0_0_8px_rgba(251,191,36,0.8)]" 
                />
              </div>
            </div>

            {/* Hard */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-rose-400 font-semibold">Hard</span>
                <span className="text-rose-400 font-bold">{hardSolved} <span className="text-gray-500 font-normal text-xs">/ {totalHard}</span></span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(hardSolved / totalHard) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
                  className="bg-rose-400 h-full rounded-full shadow-[0_0_8px_rgba(244,63,94,0.8)]" 
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Style for ripple loader and loading message */}
      <style jsx>{`
        @keyframes ripple {
          0% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        .ripple-loader {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto;
          border-radius: 50%;
          border: 5px solid rgba(255, 255, 255, 0.2);
          border-top-color: rgba(255, 255, 255, 1);
          animation: ripple 1s infinite linear;
        }

        .loading-text {
          font-size: 1.5rem;
          animation: colorChange 2s infinite;
        }

        /* Color change animation */
        @keyframes colorChange {
          0% {
            color: #ffffff;
          }
          25% {
            color: #ff4081;
          }
          50% {
            color: #3f51b5;
          }
          75% {
            color: #4caf50;
          }
          100% {
            color: #ffffff;
          }
        }
      `}</style>
    </div>
  );
};

// Loading Message Component
const LoadingMessage = () => {
  return (
    <div className="text-white text-center">
      <div className="ripple-loader"></div>
      <h2 className="loading-text">fetching data from LeetCode site...</h2>
    </div>
  );
};

export default LeetCodeDashboard;
